import React, { useState, useEffect, useMemo, useRef } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, Box } from "@radix-ui/themes";
import {
  Plus,
  Trash2,
  CheckCircle,
  CircleAlert,
  Search,
  Settings,
  Building2,
  Mail,
  Phone,
  Printer,
  Package,
  User,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useDecimalPrecision } from "@/hooks/useDecimalPrecision";
import { RATE_INPUT_STEP } from "@/utils/decimal";
import { deriveDocumentTotals } from "@/utils/documentTotals";
import {
  useCreateQuotation,
  useUpdateQuotation,
  useQuotation,
} from "@/hooks/useQuotations";
import { useLocation } from "wouter";
import { useInvoicePrefix } from "@/hooks/useInvoicePrefix";
import { SearchModal } from "@/components/modal-item";
import SearchBuyerModal from "@/components/modal-buyer";
import TinSearch from "@/components/modal-tin";
import { registrationTypes } from "@/constants/registrationTypes";
import { countries } from "@/constants/countryCodes";
import { states } from "@/constants/states";
import { classificationCodes } from "@/constants/classificationCodes";
import { UomCombobox } from "@/components/uom-combobox";
import { taxTypes } from "@/constants/taxTypes";
import { currencyCodes } from "@/constants/currencyCodes";
import { useAuth } from "@/hooks/useAuth";
import { useJobCode } from "@/hooks/userJobCode";
import type { buyerFormData, invoiceItemSchema } from "@/schema/schema";
import { useBuyers } from "@/hooks/useBuyers";
import IncompleteProfile from "@/components/incomplete-profile";
import LoadingIndicator from "@/components/LoadingIndicator";
import { printQuotation } from "@/utils/print/quotationTemplate";
import type { Quotation } from "@/types/salesDocuments";
import { gmailEmailsApi } from "@/services/gmailApi";
import { useGmailEmail } from "@/hooks/useGmailEmails";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { ExtractedData } from "@/types/gmail";

const quotationSchema = z.object({
  // Document header
  codeNumber: z.string().min(1, "Code number is required"),
  quotationDate: z.string().min(1, "Quotation date is required"),
  quotationTime: z.string().min(1, "Quotation time is required"),
  validUntilDate: z.string().nullable(),
  currencyCode: z.string().min(1, "Currency code is required"),
  currencyExchangeRate: z.number().min(0),
  docDate: z.string().nullable(),
  jobCode: z.string().nullable(),

  // Buyer Information
  buyerCode: z.string().nullable(),
  buyerName: z.string().min(1, "Buyer name is required"),
  buyerTin: z.string().nullable().optional(),
  buyerRegistrationType: z.string().nullable().optional(),
  buyerRegistrationNumber: z.string().nullable().optional(),
  buyerSstRegistrationNumber: z.string().nullable(),
  buyerEmail: z.string().nullable().optional(),
  buyerContact: z.string().min(1, "Contact number is required"),
  buyerAddressLine0: z.string().nullable().optional(),
  buyerAddressLine1: z.string().nullable(),
  buyerAddressLine2: z.string().nullable(),
  buyerCity: z.string().nullable().optional(),
  buyerPostcode: z.string().nullable(),
  buyerState: z.string().nullable().optional(),
  buyerCountry: z.string().nullable().optional(),

  // Line items
  items: z
    .array(
      z.object({
        id: z.string().optional(),
        itemCode: z.string(),
        classification: z.string(),
        description: z.string(),
        quantity: z.number().min(0.01),
        unitPrice: z.number().min(0),
        measurement: z.string().nullable(),
        discountRate: z.number().nullable(),
        discountAmount: z.number().nullable(),
        feeRate: z.number().nullable(),
        feeAmount: z.number().nullable(),
        taxType: z.enum(["01", "02", "03", "04", "05", "06", "E"]),
        taxRate: z.number().min(0),
        taxAmount: z.number().min(0),
        subtotal: z.number(),
        totalExcludingTax: z.number(),
        total: z.number(),
        detailsOfTaxExemption: z.string().nullable(),
        amountExemptedFromTax: z.number().nullable(),
        productTariffCode: z.string().max(12).optional().nullable(),
        countryOfOrigin: z.string().max(3).optional().nullable(),
        _delete: z.boolean().optional(),
      })
    )
    .min(1, "At least one line item is required"),

  // Additional charges
  quotationAdditionalDiscountAmount: z.number().nullable(),
  quotationAdditionalFeeAmount: z.number().nullable(),
  roundingAmount: z.number().nullable(),
  termsAndConditions: z.string().nullable(),
  remarks: z.string().nullable(),

  // Shipping Information (optional)
  shipping: z
    .object({
      id: z.string().nullable().optional(), // Required for updates to identify existing shipping record
      recipientName: z.string().nullable().optional(),
      recipientAddressLine0: z.string().nullable().optional(),
      recipientAddressLine1: z.string().nullable().optional(),
      recipientAddressLine2: z.string().nullable().optional(),
      recipientPostcode: z.string().nullable().optional(),
      recipientCity: z.string().nullable().optional(),
      recipientCountry: z.string().nullable().optional(),
      recipientState: z.string().nullable().optional(),
      recipientRegistrationType: z.string().nullable().optional(),
      recipientRegistrationNumber: z.string().nullable().optional(),
      recipientTin: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
});

type QuotationFormValues = z.infer<typeof quotationSchema>;

export const QuotationForm = () => {
  const { user } = useAuth();
  if (!user) return <LoadingIndicator />;
  if (user.addressId === null) return <IncompleteProfile />;

  return <QuotationFormView user={user} />;
};

const QuotationFormView = ({ user }: { user: any }) => {
  const { round, format: formatDecimal, step, precision, roundTotal, formatTotalAmount } =
    useDecimalPrecision();
  const { toast } = useToast();
  const [_, navigate] = useLocation();
  const quotationId = window.history.state?.id ?? null;

  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isSearchBuyerModalOpen, setIsSearchBuyerModalOpen] = useState(false);
  const [openTinSearch, setOpenTinSearch] = useState(false);
  const [buyerCode, setBuyerCode] = useState<string | null>(null);
  const [buyerDisplayCode, setBuyerDisplayCode] = useState<string | null>(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [totals, setTotals] = useState({
    totalExcludingTax: 0,
    totalIncludingTax: 0,
    totalTaxAmount: 0,
    totalPayableAmount: 0,
  });

  // Email-generated quotation prefill from sessionStorage
  const [isEmailGenerated, setIsEmailGenerated] = React.useState(false);
  const [sourceEmailId, setSourceEmailId] = React.useState<string | null>(null);
  const [emailDialogOpen, setEmailDialogOpen] = React.useState(false);

  const { buyers } = useBuyers();
  const { jobCode } = useJobCode();
  const { codeNumberOptions } = useInvoicePrefix("QT");
  const createQuotation = useCreateQuotation();
  const updateQuotation = useUpdateQuotation();
  const { data: existingQuotation, isLoading: isLoadingQuotation } = useQuotation(quotationId);

  // Resolve the email ID for the "View Source Email" dialog:
  // For existing quotations loaded from the API, prefer existingQuotation.sourceEmailId.
  // For new quotations just created from an email, use the state set from sessionStorage.
  const resolvedEmailId =
    existingQuotation?.sourceEmailId || sourceEmailId || null;
  const { data: sourceEmail } = useGmailEmail(
    emailDialogOpen ? resolvedEmailId ?? undefined : undefined
  );
  const [isQuotationLoading, setIsQuotationLoading] = useState<boolean>(true);
  const itemsLoadedRef = useRef<boolean>(false);
  const shippingIdRef = useRef<string | null>(null); // Store shipping ID separately

  const defaultValues = useMemo(() => {
    if (!existingQuotation) {
      return {
        codeNumber: "",
        quotationDate: format(new Date(), "yyyy-MM-dd"),
        quotationTime: format(new Date(), "HH:mm:ss"),
        validUntilDate: format(
          new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
          "yyyy-MM-dd"
        ),
        currencyCode: "MYR",
        currencyExchangeRate: 1.0,
        docDate: null,
        jobCode: null,

        buyerCode: null,
        buyerName: "",
        buyerTin: "",
        buyerRegistrationType: "",
        buyerRegistrationNumber: "",
        buyerSstRegistrationNumber: null,
        buyerEmail: "",
        buyerContact: "",
        buyerAddressLine0: "",
        buyerAddressLine1: null,
        buyerAddressLine2: null,
        buyerCity: "",
        buyerPostcode: null,
        buyerState: "",
        buyerCountry: "",

        items: [],
        quotationAdditionalDiscountAmount: null,
        quotationAdditionalFeeAmount: null,
        roundingAmount: null,
        termsAndConditions: null,
        remarks: null,
        shipping: null,
      };
    }

    const quotation = existingQuotation;

    // Set buyer code state
    setBuyerCode(quotation.buyerCode);

    const defaults = {
      codeNumber: quotation.codeNumber,
      quotationDate: format(new Date(quotation.quotationDate), "yyyy-MM-dd"),
      quotationTime: format(new Date(quotation.quotationTime), "HH:mm:ss"),
      validUntilDate: quotation.validUntil
        ? format(new Date(quotation.validUntil), "yyyy-MM-dd")
        : format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), "yyyy-MM-dd"),
      currencyCode: quotation.currencyCode,
      currencyExchangeRate: quotation.currencyExchangeRate ?? 1.0,
      docDate: quotation.quotationDate ? format(new Date(quotation.quotationDate), "yyyy-MM-dd") : null,
      jobCode: null,

      buyerCode: quotation.buyerCode,
      buyerName: quotation.buyerName,
      buyerTin: quotation.buyerTin,
      buyerRegistrationType: quotation.buyerRegistrationType,
      buyerRegistrationNumber: quotation.buyerRegistrationNumber,
      buyerSstRegistrationNumber: quotation.buyerSstRegistrationNumber,
      buyerEmail: quotation.buyerEmail || "",
      buyerContact: quotation.buyerContact,
      buyerAddressLine0: quotation.buyerAddressLine0,
      buyerAddressLine1: quotation.buyerAddressLine1,
      buyerAddressLine2: quotation.buyerAddressLine2,
      buyerCity: quotation.buyerCity,
      buyerPostcode: quotation.buyerPostcode,
      buyerState: quotation.buyerState,
      buyerCountry: quotation.buyerCountry,

      items: [], // Don't include lineItems here - they're appended in useEffect to avoid duplication
      quotationAdditionalDiscountAmount: quotation.additionalDiscountAmount,
      quotationAdditionalFeeAmount: quotation.additionalFeeAmount,
      roundingAmount: null,
      termsAndConditions: quotation.termsAndConditions,
      remarks: quotation.remarks,
      shipping: quotation.shipping || null,
    };

    return defaults;
  }, [existingQuotation]);

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useForm<QuotationFormValues>({
    resolver: zodResolver(quotationSchema),
    defaultValues,
  });

  // Load existing quotation data - similar to invoice pattern
  useEffect(() => {
    const loadQuotationDetails = async () => {
      if (!existingQuotation) {
        setIsQuotationLoading(false);
        itemsLoadedRef.current = false;
        return;
      }

      // Skip if items already loaded
      if (itemsLoadedRef.current) {
        setIsQuotationLoading(false);
        return;
      }

      setIsQuotationLoading(true);
      reset(defaultValues);

      // Store shipping ID in ref to preserve it across re-renders
      if (existingQuotation.shipping?.id) {
        shippingIdRef.current = existingQuotation.shipping.id;
      }

      // Append line items manually
      for (const item of existingQuotation.lineItems || []) {
        append({
          id: item.id,
          itemCode: item.itemCode,
          classification: item.classification,
          description: item.description || "",
          unitPrice: item.unitPrice || 0,
          taxType: item.taxType,
          taxRate: item.taxRate || 0,
          taxAmount: item.taxAmount,
          subtotal: item.subtotal,
          totalExcludingTax: item.totalExcludingTax,
          quantity: item.quantity || 1,
          discountRate: item.discountRate || 0,
          discountAmount: item.discountAmount || 0,
          feeRate: item.feeChargeRate || 0,
          feeAmount: item.feeChargeAmount || 0,
          detailsOfTaxExemption: item.detailsOfTaxExemption ?? null,
          amountExemptedFromTax: item.amountExemptedFromTax ?? null,
          productTariffCode: item.productTariffCode,
          countryOfOrigin: item.countryOfOrigin,
          measurement: item.measurement ?? null,
          total: round(item.totalExcludingTax + item.taxAmount) || 0,
        });
      }

      itemsLoadedRef.current = true;

      // Small delay to ensure UI updates smoothly
      await new Promise((resolve) => setTimeout(resolve, 100));
      setIsQuotationLoading(false);
    };

    loadQuotationDetails();
  }, [existingQuotation?.id]);

  // Resolve human-readable buyer display code from UUID when loading an existing quotation
  useEffect(() => {
    if (!existingQuotation?.buyerCode) return;
    const rawList = ((buyers as any)?.data ?? buyers) as Array<{ id: string; code: string | null }> | undefined;
    if (!rawList) return;
    const matched = rawList.find((b) => b.id === existingQuotation.buyerCode);
    if (matched?.code) setBuyerDisplayCode(matched.code);
  }, [existingQuotation?.buyerCode, buyers]);

  // Pre-fill form from email-extracted data (stored in sessionStorage by QuotationMatchingModal)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("from_email") === "true" && !quotationId) {
      const prefillRaw = sessionStorage.getItem("quotation_prefill");
      if (prefillRaw) {
        try {
          const prefill = JSON.parse(prefillRaw);
          sessionStorage.removeItem("quotation_prefill");

          if (prefill.isEmailGenerated) setIsEmailGenerated(true);
          if (prefill.sourceEmailId) setSourceEmailId(prefill.sourceEmailId);

          const buyer = prefill.buyer || {};
          setValue("buyerName", buyer.buyerName || "");
          setValue("buyerEmail", buyer.buyerEmail || "");
          setValue("buyerContact", buyer.buyerContact || "");
          setValue("buyerRegistrationType", buyer.buyerRegistrationType || "BRN");
          setValue("buyerRegistrationNumber", buyer.buyerRegistrationNumber || "");
          setValue("buyerTin", buyer.buyerTin || "EI00000000010");
          setValue("buyerAddressLine0", buyer.buyerAddressLine0 || "");
          setValue("buyerAddressLine1", buyer.buyerAddressLine1 || null);
          setValue("buyerAddressLine2", buyer.buyerAddressLine2 || null);
          setValue("buyerPostcode", buyer.buyerPostcode || null);
          setValue("buyerCity", buyer.buyerCity || "");
          setValue("buyerState", buyer.buyerState || "00");
          setValue("buyerCountry", buyer.buyerCountry || "MYS");
          if (buyer.buyerCode) {
            setValue("buyerCode", buyer.buyerCode);
            setBuyerCode(buyer.buyerCode); // UUID — used in payload FK
          }
          if (buyer.buyerDisplayCode) setBuyerDisplayCode(buyer.buyerDisplayCode); // text code for display
          if (buyer.buyerSstRegistrationNumber) setValue("buyerSstRegistrationNumber", buyer.buyerSstRegistrationNumber);

          // Pre-fill line items
          if (prefill.lineItems && prefill.lineItems.length > 0) {
            for (const item of prefill.lineItems) {
              append({
                itemCode: item.itemCode,
                classification: item.classification,
                description: item.description,
                unitPrice: item.unitPrice,
                quantity: item.quantity,
                measurement: item.measurement || "",
                discountRate: null,
                discountAmount: null,
                feeRate: null,
                feeAmount: null,
                taxType: item.taxType,
                taxRate: item.taxRate,
                taxAmount: item.taxAmount,
                detailsOfTaxExemption: null,
                amountExemptedFromTax: null,
                subtotal: item.subtotal,
                totalExcludingTax: item.totalExcludingTax,
                total: item.total,
                productTariffCode: null,
                countryOfOrigin: null,
              });
            }
          }
        } catch (e) {
          console.error("Failed to load email prefill data:", e);
        }
      }
    }
  }, []);

  // Auto-select default prefix
  useEffect(() => {
    if (codeNumberOptions && codeNumberOptions.length > 0 && !quotationId) {
      // Try to find default prefix, fallback to first option if no default
      const defaultPrefix = codeNumberOptions.find(
        (option) => option.isDefault === true
      ) || codeNumberOptions[0];

      if (defaultPrefix) {
        setValue("codeNumber", defaultPrefix.codeNumber);
      }
    }
  }, [codeNumberOptions, setValue, quotationId]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "items",
  });

  const items = watch("items");
  const quotationAdditionalDiscountAmount =
    watch("quotationAdditionalDiscountAmount") ?? 0;
  const quotationAdditionalFeeAmount =
    watch("quotationAdditionalFeeAmount") ?? 0;
  const roundingAmount = watch("roundingAmount") ?? 0;

  // Calculate line item
  const handleCalculation = (index: number, field: string, value: string | number) => {
    const items = getValues("items");
    const item = items[index];
    if (!item) return;

    const updatedItem = {
      ...item,
      [field]: Number(value) || 0,
    };

    const toSafeNumber = (value: any): number => {
      const num = Number(value);
      return isNaN(num) ? 0 : num;
    };

    const quantity = toSafeNumber(updatedItem.quantity);
    const unitPrice = toSafeNumber(updatedItem.unitPrice);
    const discountRate = toSafeNumber(updatedItem.discountRate);
    const feeRate = toSafeNumber(updatedItem.feeRate);

    const subtotal = quantity * unitPrice;
    const discountAmount = subtotal * (discountRate / 100);
    const taxableAmount = subtotal - discountAmount;
    const feeChargeAmount = taxableAmount * (feeRate / 100);

    const taxRate = Number(updatedItem.taxRate ?? 0);
    const taxAmount = taxableAmount * (taxRate / 100);

    const totalExcludingTax = taxableAmount + feeChargeAmount;
    const total = totalExcludingTax + taxAmount;

    setValue(`items.${index}.subtotal`, round(subtotal), {
      shouldValidate: true,
    });
    setValue(
      `items.${index}.discountAmount`,
      round(discountAmount),
      { shouldValidate: true }
    );
    setValue(`items.${index}.feeAmount`, round(feeChargeAmount), {
      shouldValidate: true,
    });
    setValue(`items.${index}.taxAmount`, round(taxAmount), {
      shouldValidate: true,
    });
    setValue(
      `items.${index}.totalExcludingTax`,
      round(totalExcludingTax),
      { shouldValidate: true }
    );
    setValue(`items.${index}.total`, round(total));

    // Manually trigger totals recalculation after updating line item
    const allItems = getValues("items") || [];
    const calculatedTotals = allItems.reduce(
      (acc, item) => {
        const totalExcludingTax = Number(item.totalExcludingTax) || 0;
        const total = Number(item.total) || 0;
        const taxAmount = Number(item.taxAmount) || 0;

        return {
          totalExcludingTax: acc.totalExcludingTax + totalExcludingTax,
          totalIncludingTax: acc.totalIncludingTax + total,
          totalTaxAmount: acc.totalTaxAmount + taxAmount,
          totalPayableAmount: acc.totalPayableAmount + total,
        };
      },
      {
        totalExcludingTax: 0,
        totalIncludingTax: 0,
        totalTaxAmount: 0,
        totalPayableAmount: 0,
      }
    );

    setTotals(
      deriveDocumentTotals({
        rawExcludingTax: calculatedTotals.totalExcludingTax,
        totalTaxAmount: calculateTotalTaxPerType().reduce(
          (sum, entry) => sum + entry.taxAmount,
          0,
        ),
        additionalDiscountAmount: quotationAdditionalDiscountAmount,
        additionalFeeAmount: quotationAdditionalFeeAmount,
        roundingAmount,
      }),
    );
  };

  // Update totals
  useEffect(() => {
    if (!items || items.length === 0) {
      setTotals({
        totalExcludingTax: 0,
        totalIncludingTax: 0,
        totalTaxAmount: 0,
        totalPayableAmount: 0,
      });
      return;
    }

    const totals = items.reduce(
      (acc, item) => {
        return {
          totalExcludingTax: acc.totalExcludingTax + (item.totalExcludingTax || 0),
          totalIncludingTax: acc.totalIncludingTax + (item.total || 0),
          totalTaxAmount: acc.totalTaxAmount + (item.taxAmount || 0),
          totalPayableAmount: acc.totalPayableAmount + (item.total || 0),
        };
      },
      {
        totalExcludingTax: 0,
        totalIncludingTax: 0,
        totalTaxAmount: 0,
        totalPayableAmount: 0,
      }
    );

    setTotals(
      deriveDocumentTotals({
        rawExcludingTax: totals.totalExcludingTax,
        totalTaxAmount: calculateTotalTaxPerType().reduce(
          (sum, entry) => sum + entry.taxAmount,
          0,
        ),
        additionalDiscountAmount: quotationAdditionalDiscountAmount,
        additionalFeeAmount: quotationAdditionalFeeAmount,
        roundingAmount,
      }),
    );
  }, [items, quotationAdditionalDiscountAmount, quotationAdditionalFeeAmount, roundingAmount]);

  const calculateTotalTaxPerType = () => {
    const items = getValues("items") || [];

    const map = new Map<
      "01" | "02" | "03" | "04" | "05" | "06" | "E",
      number
    >();

    for (const item of items) {
      if (!item || item._delete) continue;

      const taxType = item.taxType;
      const taxAmount = Number(item.taxAmount ?? 0);

      if (!taxType) continue;

      map.set(taxType, (map.get(taxType) || 0) + taxAmount);
    }

    const grouped = Array.from(map.entries()).map(([taxType, taxAmount]) => ({
      taxType,
      taxAmount: roundTotal(taxAmount),
    }));

    return grouped;
  };

  const handleSelectBuyer = (buyer: buyerFormData) => {
    setBuyerCode(buyer.id);          // UUID — stored as FK in buyer_code column
    setBuyerDisplayCode(buyer.code); // Human-readable code for display only
    setValue("buyerRegistrationType", buyer.registrationType);
    setValue("buyerRegistrationNumber", buyer.registrationNumber);
    setValue("buyerTin", buyer.tin);
    setValue("buyerName", buyer.name);
    setValue("buyerSstRegistrationNumber", buyer.sstRegistrationNumber);
    setValue("buyerEmail", buyer.email);
    setValue("buyerContact", buyer.contactNumber);
    setValue("buyerAddressLine0", buyer.buyerAddress.addressLine0);
    setValue("buyerAddressLine1", buyer.buyerAddress.addressLine1);
    setValue("buyerAddressLine2", buyer.buyerAddress.addressLine2);
    setValue("buyerCity", buyer.buyerAddress.cityName);
    setValue("buyerPostcode", buyer.buyerAddress.postalZone);
    setValue("buyerState", buyer.buyerAddress.state);
    setValue("buyerCountry", buyer.buyerAddress.country);
  };

  const handleSelectItem = (item: z.infer<typeof invoiceItemSchema>) => {
    const newIndex = fields.length;
    const mappedItem = {
      ...item,
      itemCode: item.codeNumber,
    };
    append(mappedItem as any);
    // Use setTimeout to allow RHF to register the appended item before calculating
    setTimeout(() => handleCalculation(newIndex, "quantity", item.quantity || 1), 0);
  };

  const onSubmit = async (data: QuotationFormValues) => {
    try {
      const prefixId = codeNumberOptions?.find(
        (option) => option.codeNumber === data.codeNumber
      )?.id;

      const lineItems = data.items.map((item) => ({
        id: item.id, // Include ID for updates to identify existing line items
        itemCode: item.itemCode,
        classification: item.classification,
        description: item.description,
        unitPrice: Number(item.unitPrice),
        taxType: item.taxType,
        taxRate: Number(item.taxRate),
        taxAmount: Number(item.taxAmount),
        detailsOfTaxExemption: item.detailsOfTaxExemption || null,
        amountExemptedFromTax: item.amountExemptedFromTax || null,
        subtotal: item.subtotal,
        totalExcludingTax: item.totalExcludingTax,
        total: item.total,
        quantity: Number(item.quantity),
        measurement: item.measurement || null,
        discountRate: Number(item.discountRate) || null,
        discountAmount: item.discountAmount || null,
        feeRate: Number(item.feeRate) || null,
        feeAmount: item.feeAmount || null,
        productTariffCode: item.productTariffCode || null,
        countryOfOrigin: item.countryOfOrigin || null,
      }));

      const payload = {
        // Buyer Information
        buyerCode: buyerCode,
        buyerRegistrationType: data.buyerRegistrationType!,
        buyerRegistrationNumber: data.buyerRegistrationNumber!,
        buyerTin: data.buyerTin!,
        buyerName: data.buyerName,
        buyerSstRegistrationNumber: data.buyerSstRegistrationNumber,
        buyerEmail: data.buyerEmail,
        buyerContact: data.buyerContact,
        buyerAddressLine0: data.buyerAddressLine0,
        buyerAddressLine1: data.buyerAddressLine1,
        buyerAddressLine2: data.buyerAddressLine2,
        buyerCity: data.buyerCity,
        buyerPostcode: data.buyerPostcode,
        buyerState: data.buyerState,
        buyerCountry: data.buyerCountry,

        // Document Details
        codeNumber: data.codeNumber,
        quotationDate: new Date().toISOString(),
        quotationTime: new Date().toISOString(),
        validUntilDate: data.validUntilDate ? new Date(data.validUntilDate).toISOString() : null,
        currencyCode: data.currencyCode,
        currencyExchangeRate: Number(data.currencyExchangeRate),
        docDate: data.docDate ? new Date(data.docDate) : null,
        jobCode: data.jobCode,

        // Line Items
        lineItems: lineItems,

        // Shipping - inject the ID from ref if it exists
        shipping: data.shipping ? {
          ...data.shipping,
          id: shippingIdRef.current || data.shipping.id
        } : {},

        // Totals
        totalExcludingTax: roundTotal(totals.totalExcludingTax),
        totalIncludingTax: roundTotal(totals.totalIncludingTax),
        totalPayableAmount: roundTotal(totals.totalPayableAmount),
        totalNetAmount: null,
        totalDiscountValue: null,
        totalFeeChargeAmount: null,

        // Tax
        totalTaxAmount: roundTotal(totals.totalTaxAmount),
        roundingAmount: data.roundingAmount,
        totalTaxableAmountPerTaxType: null,
        totalTaxAmountPerTaxType: calculateTotalTaxPerType(),
        taxType: "06" as const,

        // Additional charges
        quotationAdditionalDiscountAmount: data.quotationAdditionalDiscountAmount,
        quotationAdditionalFeeAmount: data.quotationAdditionalFeeAmount,

        // Notes
        termsAndConditions: data.termsAndConditions,
        remarks: data.remarks,

        // Email-generated quotation metadata
        isEmailGenerated: isEmailGenerated || false,
        sourceEmailId: sourceEmailId || null,

        prefixId,
      };

      if (quotationId) {
        await updateQuotation.mutateAsync(
          { id: quotationId, data: payload as any },
          {
            onSuccess: () => {
              toast({
                title: "Quotation Updated",
                description: `The quotation ${data.codeNumber} successfully updated.`,
                duration: 5000,
                icon: (
                  <CheckCircle
                    size={30}
                    className="animate-spin-once text-green-400"
                  />
                ),
              });
              navigate("/quotations");
            },
            onError: (error: any) => {
              const errorMessage =
                error?.response?.data?.message ||
                error?.message ||
                "Failed to update quotation. Please try again.";
              toast({
                title: "Failed",
                variant: "destructive",
                description: errorMessage,
                icon: <CircleAlert size={30} className="animate-spin-once" />,
                duration: 4000,
              });
            },
          }
        );
      } else {
        await createQuotation.mutateAsync(payload as any, {
          onSuccess: (apiResult: any) => {
            // Mark the source email as converted if this quotation was created from an email
            if (isEmailGenerated && sourceEmailId && apiResult?.id) {
              gmailEmailsApi.convert(sourceEmailId, apiResult.id).catch(console.error);
            }
            toast({
              title: "Quotation Saved",
              description: `The quotation ${data.codeNumber} successfully saved.`,
              duration: 5000,
              icon: (
                <CheckCircle
                  size={30}
                  className="animate-spin-once text-green-400"
                />
              ),
            });
            navigate("/quotations");
          },
          onError: (error: any) => {
            const errorMessage =
              error?.response?.data?.message ||
              error?.message ||
              "Failed to save quotation. Please try again.";
            toast({
              title: "Failed",
              variant: "destructive",
              description: errorMessage,
              icon: <CircleAlert size={30} className="animate-spin-once" />,
              duration: 4000,
            });
          },
        });
      }
    } catch (error: any) {
      console.error("Error creating quotation:", error);

      // Extract validation errors from backend response
      let errorMessage = "Failed to save quotation. Please check all fields.";

      if (error?.response?.data?.details && Array.isArray(error.response.data.details)) {
        // Zod validation errors with details array
        const validationErrors = error.response.data.details
          .map((err: any) => `${err.path}: ${err.message}`)
          .join('\n');
        errorMessage = `Validation failed:\n${validationErrors}`;
      } else if (error?.response?.data?.message) {
        // Generic error message
        errorMessage = error.response.data.message;
      } else if (error?.response?.data?.error) {
        // Error object
        const backendError = error.response.data.error;
        if (typeof backendError === 'string') {
          errorMessage = backendError;
        } else if (backendError.message) {
          errorMessage = backendError.message;
        }
      } else if (error?.message) {
        // Axios error message
        errorMessage = error.message;
      }

      toast({
        title: "Failed to save quotation",
        variant: "destructive",
        description: errorMessage,
        icon: <CircleAlert size={30} />,
        duration: 6000,
      });
    }
  };

  const onError = (errors: any) => {
    // Collect all error messages
    const errorMessages: string[] = [];

    const collectErrors = (obj: any, prefix = "") => {
      Object.keys(obj).forEach((key) => {
        const fullKey = prefix ? `${prefix}.${key}` : key;
        if (obj[key]?.message) {
          errorMessages.push(`${fullKey}: ${obj[key].message}`);
        } else if (typeof obj[key] === "object" && obj[key] !== null) {
          collectErrors(obj[key], fullKey);
        }
      });
    };

    collectErrors(errors);

    const description = errorMessages.length > 0
      ? errorMessages.join("\n")
      : "Please fill in all required fields";

    toast({
      title: "Action failed",
      variant: "destructive",
      description: description,
      icon: <CircleAlert size={30} className="animate-spin-once" />,
      duration: 8000,
    });
  };


  const handlePrint = async () => {
    if (!existingQuotation || !quotationId) {
      toast({
        title: "Cannot Print",
        variant: "destructive",
        description: "Please save the quotation first before printing",
        icon: <CircleAlert size={30} />,
        duration: 4000,
      });
      return;
    }

    setIsPrinting(true);

    try {
      // Prepare quotation data for printing
      const quotationData: Quotation = {
        ...existingQuotation,
        // Ensure all required fields are present
        lineItems: existingQuotation.lineItems || [],
        termsAndConditions: existingQuotation.termsAndConditions || null,
        remarks: existingQuotation.remarks || null,
      };

      const companyInfo = {
        businessName: user.businessName,
        addressLine0: user.addressLine0,
        addressLine1: user.addressLine1 || undefined,
        addressLine2: user.addressLine2 || undefined,
        cityName: user.cityName,
        postalZone: user.postalZone,
        state: user.state,
        country: user.country,
        contactNumber: user.contactNumber,
        email: user.email,
        tin: user.tin,
        registrationNumber: user.registrationNumber,
        companyLogo: user.companyLogo || undefined,
        reportFooter: user.reportFooter || undefined,
      };

      await printQuotation({
        precision,
        quotation: quotationData,
        companyInfo,
      });
    } catch (error: any) {
      console.error("Error printing quotation:", error);
      toast({
        title: "Print Failed",
        variant: "destructive",
        description: error?.response?.data?.message || error?.message || "Failed to generate print preview. Please try again.",
        icon: <CircleAlert size={30} />,
        duration: 4000,
      });
    } finally {
      setIsPrinting(false);
    }
  };

  // Utility function reserved for future use
  // const getLocalDateTimeString = () => {
  //   const now = new Date();
  //   const offset = now.getTimezoneOffset();
  //   const localDate = new Date(now.getTime() - offset * 60 * 1000);
  //   return localDate.toISOString().slice(0, 16);
  // };

  if (isQuotationLoading || isLoadingQuotation) {
    return <LoadingIndicator />;
  }

  return (
    <Card className="w-full max-w-6xl mx-auto shadow p-2 pt-6">
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit, onError)} className="space-y-6">
          <div className="flex flex-col md:flex-row justify-between items-center md:items-start gap-6 md:gap-0">
            <div className="static md:absolute flex flex-row">
              <h1 className="text-xl font-bold text-center md:text-left">
                {quotationId ? "Edit" : "Create"} Quotation
              </h1>
            </div>

            {/* Company Details - Middle */}
            <div className="flex flex-col items-center justify-center w-full text-center space-y-3">
              <div className="flex flex-row items-center justify-center gap-4">
                <h1 className="text-2xl font-bold text-center md:text-left">
                  {user.businessName}
                </h1>
              </div>

              <div className="flex items-center space-x-2 text-gray-600">
                <Phone size={16} className="flex-shrink-0" />
                <span className="text-sm">{user.contactNumber}</span>
              </div>

              <div className="flex items-center space-x-2 text-gray-600">
                <Mail size={16} className="flex-shrink-0" />
                <span className="text-sm">{user.email}</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-600">
                <Building2 size={16} className="flex-shrink-0" />
                <span className="text-sm">
                  {user.addressLine0}, {user.addressLine1 && user.addressLine1 + ", "}{" "}
                  {user.addressLine2 && user.addressLine2 + ", "} {user.cityName}
                </span>
              </div>
            </div>
          </div>

          {/* Email-generated quotation banner */}
          {(isEmailGenerated || existingQuotation?.isEmailGenerated) && (
            <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200 dark:bg-blue-950/20 dark:border-blue-900/50">
              <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-900 dark:text-blue-100">Generated from Email</p>
                <p className="text-xs text-blue-600 dark:text-blue-400">This quotation was created from an email quotation request.</p>
              </div>
              {resolvedEmailId && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setEmailDialogOpen(true)}
                  className="flex items-center gap-2 border-blue-300 dark:border-blue-700"
                >
                  <Mail className="w-4 h-4" />
                  View Source Email
                </Button>
              )}
            </div>
          )}

          <Tabs.Root defaultValue="buyerInfo">
            <Tabs.List color="gray" justify="start">
              <Tabs.Trigger value="buyerInfo">
                Buyer Information<span className="text-red-600">*</span>
              </Tabs.Trigger>
              <Tabs.Trigger value="quotationDetail">
                Quotation Detail<span className="text-red-600">*</span>
              </Tabs.Trigger>
              <Tabs.Trigger value="shipping">Shipping</Tabs.Trigger>
            </Tabs.List>

            <Box pt="3">
              {/* Buyer Information Tab */}
              <Tabs.Content value="buyerInfo">
                <div className="space-y-4">
                  <div className="flex flex-row gap-2 items-center">
                    <h3 className="text-lg font-semibold">
                      Buyer Information{" "}
                    </h3>
                    <Button
                      onClick={() => setIsSearchBuyerModalOpen(true)}
                      type="button"
                      variant="outline"
                      size="sm"
                    >
                      Find
                      <Search />
                    </Button>
                  </div>

                  <SearchBuyerModal
                    isOpen={isSearchBuyerModalOpen}
                    onClose={() => setIsSearchBuyerModalOpen(false)}
                    onSelectBuyer={handleSelectBuyer}
                  />

                  {buyerDisplayCode && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span className="font-medium text-foreground">Customer Code:</span>
                      <span className="font-mono bg-muted px-2 py-0.5 rounded text-foreground">{buyerDisplayCode}</span>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="buyerRegistrationType" required>
                        ID Type
                      </Label>
                      <Controller
                        name="buyerRegistrationType"
                        control={control}
                        render={({ field }) => (
                          <Select
                            onValueChange={field.onChange}
                            value={field.value ?? undefined}
                          >
                            <SelectTrigger className="h-10">
                              <SelectValue placeholder="-- Select type --" />
                            </SelectTrigger>
                            <SelectContent>
                              {registrationTypes.map((type) => (
                                <SelectItem key={type.value} value={type.value}>
                                  {type.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                      {errors.buyerRegistrationType && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.buyerRegistrationType.message?.toString()}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="buyerRegistrationNumber" required>
                        Registration / Identification / Passport Number
                      </Label>
                      <Input {...register("buyerRegistrationNumber")} />
                      {errors.buyerRegistrationNumber && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.buyerRegistrationNumber.message?.toString()}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="buyerTin" required>
                        TIN
                      </Label>
                      <div className="flex gap-1">
                        <Input {...register("buyerTin")} />
                        <Button
                          variant="outline"
                          type="button"
                          onClick={() => setOpenTinSearch(true)}
                          className="group"
                        >
                          <Settings />
                        </Button>
                      </div>
                      {errors.buyerTin && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.buyerTin.message?.toString()}
                        </p>
                      )}
                    </div>
                    <TinSearch
                      isOpen={openTinSearch}
                      onClose={() => setOpenTinSearch(false)}
                      onSelect={(value) => setValue("buyerTin", value)}
                    />

                    <div>
                      <Label htmlFor="buyerSstRegistrationNumber">
                        SST Registration Number
                      </Label>
                      <Input {...register("buyerSstRegistrationNumber")} />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="buyerName" required>
                        Name
                      </Label>
                      <Input {...register("buyerName")} />
                      {errors.buyerName && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.buyerName.message?.toString()}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="buyerEmail" required>
                        Email
                      </Label>
                      <Input type="email" {...register("buyerEmail")} />
                      {errors.buyerEmail && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.buyerEmail.message?.toString()}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="buyerContact" required>
                        Contact Number
                      </Label>
                      <Input {...register("buyerContact")} />
                      {errors.buyerContact && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.buyerContact.message?.toString()}
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="buyerAddressLine0" required>
                      Address Line 1
                    </Label>
                    <Input {...register("buyerAddressLine0")} />
                    {errors.buyerAddressLine0 && (
                      <p className="text-red-600 text-sm mt-1">
                        {errors.buyerAddressLine0.message?.toString()}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="buyerAddressLine1">Address Line 2</Label>
                    <Input {...register("buyerAddressLine1")} />
                  </div>

                  <div>
                    <Label htmlFor="buyerAddressLine2">Address Line 3</Label>
                    <Input {...register("buyerAddressLine2")} />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="buyerCity" required>
                        City
                      </Label>
                      <Input {...register("buyerCity")} />
                      {errors.buyerCity && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.buyerCity.message?.toString()}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="buyerPostcode">Postcode</Label>
                      <Input {...register("buyerPostcode")} />
                    </div>

                    <div>
                      <Label htmlFor="buyerCountry" required>
                        Country
                      </Label>
                      <Controller
                        name="buyerCountry"
                        control={control}
                        render={({ field }) => (
                          <Select
                            onValueChange={field.onChange}
                            value={field.value ?? undefined}
                          >
                            <SelectTrigger className="h-10">
                              <SelectValue placeholder="-- Select Country --" />
                            </SelectTrigger>
                            <SelectContent>
                              {countries.map((country) => (
                                <SelectItem
                                  key={country.Code}
                                  value={country.Code}
                                >
                                  {country.Country}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                      {errors.buyerCountry && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.buyerCountry.message?.toString()}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="buyerState" required>
                        State
                      </Label>
                      <Controller
                        name="buyerState"
                        control={control}
                        render={({ field }) => (
                          <Select
                            onValueChange={field.onChange}
                            value={field.value ?? undefined}
                          >
                            <SelectTrigger className="h-10">
                              <SelectValue placeholder="-- Select State --" />
                            </SelectTrigger>
                            <SelectContent>
                              {states.map((state) => (
                                <SelectItem key={state.Code} value={state.Code}>
                                  {state.State}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                      {errors.buyerState && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.buyerState.message?.toString()}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </Tabs.Content>

              {/* Quotation Detail Tab */}
              <Tabs.Content value="quotationDetail">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Quotation Detail</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="codeNumber" required>
                        Quotation Number
                      </Label>
                      {quotationId ? (
                        <Input {...register("codeNumber")} disabled />
                      ) : (
                        <Controller
                          name="codeNumber"
                          control={control}
                          render={({ field }) => (
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <SelectTrigger>
                                <SelectValue
                                  placeholder={
                                    codeNumberOptions?.find(
                                      (option) => option.isDefault === true
                                    )?.codeNumber ||
                                    "No available prefix number added"
                                  }
                                />
                              </SelectTrigger>
                              <SelectContent className="max-h-48">
                                {codeNumberOptions?.map((option) => (
                                  <SelectItem
                                    key={option.id}
                                    value={option.codeNumber}
                                  >
                                    {option.codeNumber}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                      )}
                      {errors.codeNumber && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.codeNumber.message?.toString()}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="docDate">Quotation Date</Label>
                      <Input type="date" {...register("docDate")} />
                    </div>

                    <div>
                      <Label htmlFor="validUntilDate">Valid Until</Label>
                      <Input type="date" {...register("validUntilDate")} />
                    </div>

                    <div>
                      <Label htmlFor="jobCode">Job Code</Label>
                      <Controller
                        name="jobCode"
                        control={control}
                        render={({ field }) => (
                          <Select
                            onValueChange={field.onChange}
                            value={field.value || undefined}
                          >
                            <SelectTrigger className="h-10">
                              <SelectValue
                                placeholder={
                                  jobCode
                                    ? "-- Select job code --"
                                    : "No job code available"
                                }
                              />
                            </SelectTrigger>
                            <SelectContent className="max-h-48 max-w-full">
                              {jobCode &&
                                Array.isArray(jobCode) &&
                                jobCode.map((item) => (
                                  <SelectItem key={item.code} value={item.code}>
                                    {item.code}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>

                    <div>
                      <Label htmlFor="currencyCode">Currency Code</Label>
                      <Controller
                        name="currencyCode"
                        control={control}
                        render={({ field }) => (
                          <Select
                            onValueChange={field.onChange}
                            value={field.value || undefined}
                          >
                            <SelectTrigger className="h-10">
                              <SelectValue placeholder="-- Select type --" />
                            </SelectTrigger>
                            <SelectContent>
                              {currencyCodes.map((currency) => (
                                <SelectItem
                                  key={currency.Code}
                                  value={currency.Code}
                                >
                                  {currency.Currency}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>

                    <div>
                      <Label htmlFor="currencyExchangeRate">
                        Currency Exchange Rate
                      </Label>
                      <Input
                        type="number"
                        step="0.0001"
                        {...register("currencyExchangeRate", {
                          valueAsNumber: true,
                        })}
                      />
                    </div>
                  </div>

                  {/* Terms and Conditions */}
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label htmlFor="termsAndConditions">
                        Terms & Conditions
                      </Label>
                      <Textarea
                        {...register("termsAndConditions")}
                        placeholder="Enter terms and conditions..."
                        rows={4}
                        className="resize-none"
                      />
                    </div>

                    <div>
                      <Label htmlFor="remarks">Remarks</Label>
                      <Textarea
                        {...register("remarks")}
                        placeholder="Enter remarks..."
                        rows={3}
                        className="resize-none"
                      />
                    </div>
                  </div>
                </div>
              </Tabs.Content>

              {/* Shipping Tab */}
              <Tabs.Content value="shipping">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold">
                      Shipping Information
                    </h3>
                    <h5 className="text-sm font-light">
                      Applicable to transactions where goods are shipped to a
                      different recipient and/or address (i.e., different from
                      the Buyer's details)
                    </h5>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="shipping.recipientName">
                        Shipping Recipient's Name
                      </Label>
                      <Input {...register("shipping.recipientName")} />
                    </div>
                    <div>
                      <Label htmlFor="shipping.recipientAddressLine0">
                        Address Line 1
                      </Label>
                      <Input {...register("shipping.recipientAddressLine0")} />
                    </div>
                    <div>
                      <Label htmlFor="shipping.recipientAddressLine1">
                        Address Line 2
                      </Label>
                      <Input {...register("shipping.recipientAddressLine1")} />
                    </div>
                    <div>
                      <Label htmlFor="shipping.recipientAddressLine2">
                        Address Line 3
                      </Label>
                      <Input {...register("shipping.recipientAddressLine2")} />
                    </div>
                    <div>
                      <Label htmlFor="shipping.recipientPostcode">
                        Postcode
                      </Label>
                      <Input {...register("shipping.recipientPostcode")} />
                    </div>
                    <div>
                      <Label htmlFor="shipping.recipientCity">City</Label>
                      <Input {...register("shipping.recipientCity")} />
                    </div>
                    <div>
                      <Label htmlFor="shipping.recipientCountry">Country</Label>
                      <Controller
                        name="shipping.recipientCountry"
                        control={control}
                        render={({ field }) => (
                          <Select
                            value={field.value || undefined}
                            onValueChange={field.onChange}
                          >
                            <SelectTrigger className="h-10">
                              <SelectValue placeholder="-- Select country --" />
                            </SelectTrigger>
                            <SelectContent>
                              {countries.map((country) => (
                                <SelectItem
                                  key={country.Code}
                                  value={country.Code}
                                >
                                  {country.Country}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>
                    <div>
                      <Label htmlFor="shipping.recipientState">State</Label>
                      <Controller
                        name="shipping.recipientState"
                        control={control}
                        render={({ field }) => (
                          <Select
                            value={field.value || undefined}
                            onValueChange={field.onChange}
                          >
                            <SelectTrigger className="h-10">
                              <SelectValue placeholder="-- Select state --" />
                            </SelectTrigger>
                            <SelectContent>
                              {states.map((state) => (
                                <SelectItem key={state.Code} value={state.Code}>
                                  {state.State}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>
                    <div>
                      <Label htmlFor="shipping.recipientRegistrationType">
                        ID Type
                      </Label>
                      <Controller
                        name="shipping.recipientRegistrationType"
                        control={control}
                        render={({ field }) => (
                          <Select
                            value={field.value || undefined}
                            onValueChange={field.onChange}
                          >
                            <SelectTrigger className="h-10">
                              <SelectValue placeholder="-- Select type --" />
                            </SelectTrigger>
                            <SelectContent>
                              {registrationTypes.map((type) => (
                                <SelectItem key={type.value} value={type.value}>
                                  {type.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>
                    <div>
                      <Label htmlFor="shipping.recipientRegistrationNumber">
                        Registration / Identification / Passport Number
                      </Label>
                      <Input
                        {...register("shipping.recipientRegistrationNumber")}
                      />
                    </div>
                    <div>
                      <Label htmlFor="shipping.recipientTin">
                        Shipping Recipient's TIN
                      </Label>
                      <Input {...register("shipping.recipientTin")} />
                    </div>
                  </div>
                </div>
              </Tabs.Content>

            </Box>
          </Tabs.Root>

          {/* Source Email Dialog */}
          <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
            <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  {sourceEmail?.emailSubject || "Source Email"}
                </DialogTitle>
                {sourceEmail && (
                  <p className="text-sm text-muted-foreground">
                    From: {sourceEmail.emailFrom} &middot;{" "}
                    {format(new Date(sourceEmail.emailDate), "dd MMM yyyy, HH:mm")}
                  </p>
                )}
              </DialogHeader>

              {!sourceEmail ? (
                <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
                  Loading email...
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pt-2">
                  {/* Email Content */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <Mail className="h-4 w-4" />
                      Email Content
                    </div>
                    <div className="rounded-md bg-muted p-3 text-xs font-mono whitespace-pre-wrap max-h-96 overflow-y-auto">
                      {sourceEmail.rawText || sourceEmail.emailSnippet || "(No content)"}
                    </div>
                  </div>

                  <div className="space-y-4">
                    {/* Extracted Buyer */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <User className="h-4 w-4" />
                        Extracted Buyer Info
                      </div>
                      <div className="rounded-md border p-3 text-sm space-y-1">
                        {sourceEmail.extractedData?.buyer ? (
                          Object.entries(sourceEmail.extractedData.buyer as unknown as Record<string, unknown>)
                            .filter(([, v]) => v)
                            .map(([key, value]) => (
                              <div key={key} className="flex gap-2">
                                <span className="text-muted-foreground w-28 shrink-0 capitalize text-xs">
                                  {key.replace(/_/g, " ").replace("buyer ", "")}:
                                </span>
                                <span className="text-xs font-medium">{String(value)}</span>
                              </div>
                            ))
                        ) : (
                          <p className="text-xs text-muted-foreground">No buyer data extracted.</p>
                        )}
                      </div>
                    </div>

                    {/* Extracted Line Items */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <Package className="h-4 w-4" />
                        Extracted Line Items ({(sourceEmail.extractedData as ExtractedData | null)?.line_items?.length || 0})
                      </div>
                      <div className="space-y-2">
                        {(sourceEmail.extractedData as ExtractedData | null)?.line_items?.length ? (
                          (sourceEmail.extractedData as ExtractedData).line_items.map((item, idx) => (
                            <div key={idx} className="rounded-md border p-2 text-xs space-y-0.5">
                              <div className="font-medium">{item.item_desc || "—"}</div>
                              <div className="text-muted-foreground flex gap-3">
                                {item.qty && <span>Qty: {item.qty}</span>}
                                {item.unit_price_myr && <span>Price: MYR {item.unit_price_myr}</span>}
                                {item.product_code && <span>Code: {item.product_code}</span>}
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-xs text-muted-foreground">No line items extracted.</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Line Items */}
          <div className="space-y-4 mt-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">
                Items <span className="text-red-600">*</span>
              </h3>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsSearchModalOpen(true)}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Item
              </Button>
            </div>

            <SearchModal
              isOpen={isSearchModalOpen}
              onClose={() => setIsSearchModalOpen(false)}
              onSelectItem={handleSelectItem}
            />

            {fields.length > 0 ? (
              <div className="overflow-x-auto border rounded-md">
                <Table>
                  <TableHeader className="bg-slate-50 dark:bg-slate-800">
                    <TableRow>
                      <TableHead>Item Code</TableHead>
                      <TableHead>Classification</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Unit Price</TableHead>
                      <TableHead>Quantity</TableHead>
                      <TableHead>Measurement</TableHead>
                      <TableHead>Disc. Rate</TableHead>
                      <TableHead>Tax Type</TableHead>
                      <TableHead>Tax Rate</TableHead>
                      <TableHead>Tax Amount</TableHead>
                      <TableHead>Total Excl Tax</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {fields.map((field, index) => (
                      <TableRow key={field.id}>
                        <TableCell className="text-sm font-mono text-muted-foreground whitespace-nowrap">
                          {items[index]?.itemCode || "—"}
                        </TableCell>
                        <TableCell>
                          <Controller
                            name={`items.${index}.classification`}
                            control={control}
                            render={({ field }) => (
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                              >
                                <SelectTrigger className="h-8 w-24">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {classificationCodes.map((code) => (
                                    <SelectItem
                                      key={code.Code}
                                      value={code.Code}
                                    >
                                      {code.Code}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          />
                        </TableCell>
                        <TableCell className="min-w-[200px]">
                          <Input
                            {...register(`items.${index}.description`)}
                            className="h-8"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step={step}
                            {...register(`items.${index}.unitPrice`, {
                              valueAsNumber: true,
                            })}
                            onChange={(e) => {
                              register(`items.${index}.unitPrice`).onChange(e);
                              setTimeout(
                                () => handleCalculation(index, "unitPrice", e.target.value),
                                0
                              );
                            }}
                            className="h-8 w-24"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step={step}
                            {...register(`items.${index}.quantity`, {
                              valueAsNumber: true,
                            })}
                            onChange={(e) => {
                              register(`items.${index}.quantity`).onChange(e);
                              setTimeout(
                                () => handleCalculation(index, "quantity", e.target.value),
                                0
                              );
                            }}
                            className="h-8 w-20"
                          />
                        </TableCell>
                        <TableCell>
                          <Controller
                            control={control}
                            name={`items.${index}.measurement`}
                            render={({ field }) => (
                              <UomCombobox
                                value={field.value}
                                onChange={field.onChange}
                                className="h-8 w-40"
                              />
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step={RATE_INPUT_STEP}
                            {...register(`items.${index}.discountRate`, {
                              valueAsNumber: true,
                            })}
                            onChange={(e) => {
                              register(`items.${index}.discountRate`).onChange(e);
                              setTimeout(
                                () =>
                                  handleCalculation(
                                    index,
                                    "discountRate",
                                    e.target.value
                                  ),
                                0
                              );
                            }}
                            className="h-8 w-20"
                          />
                        </TableCell>
                        <TableCell>
                          <Controller
                            name={`items.${index}.taxType`}
                            control={control}
                            render={({ field }) => (
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                              >
                                <SelectTrigger className="h-8 w-20">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {taxTypes.map((type) => (
                                    <SelectItem
                                      key={type.Code}
                                      value={type.Code}
                                    >
                                      {type.Code}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step={RATE_INPUT_STEP}
                            {...register(`items.${index}.taxRate`, {
                              valueAsNumber: true,
                            })}
                            onChange={(e) => {
                              register(`items.${index}.taxRate`).onChange(e);
                              setTimeout(
                                () => handleCalculation(index, "taxRate", e.target.value),
                                0
                              );
                            }}
                            className="h-8 w-20"
                          />
                        </TableCell>
                        <TableCell>
                          {formatDecimal(items[index]?.taxAmount || 0)}
                        </TableCell>
                        <TableCell>
                          {formatDecimal(items[index]?.totalExcludingTax || 0)}
                        </TableCell>
                        <TableCell>
                          {formatDecimal(items[index]?.total || 0)}
                        </TableCell>
                        <TableCell>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => remove(index)}
                            className="h-8 w-8 p-0 text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground border rounded-md">
                No items added. Click "Add Item" to add items to the quotation.
              </div>
            )}

            {/* Totals Section - Always visible like invoice form */}
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Total Excluding Tax:</span>
                <span>RM {formatTotalAmount(Number(totals?.totalExcludingTax || 0))}</span>
              </div>
              <div className="flex justify-between">
                <span>Total Including Tax:</span>
                <span>RM {formatTotalAmount(Number(totals?.totalIncludingTax || 0))}</span>
              </div>
              <div className="flex justify-between">
                <span>Total Tax Amount:</span>
                <span>RM {formatTotalAmount(Number(totals?.totalTaxAmount || 0))}</span>
              </div>
              <div className="flex justify-between">
                <span className="content-center">Quotation Additional Discount Amount:</span>
                <span>
                  <Input
                    className="text-right h-8"
                    type="number"
                    step={step}
                    {...register("quotationAdditionalDiscountAmount", {
                      setValueAs: (value) => Number(value)
                    })}
                  />
                </span>
              </div>
              <div className="flex justify-between">
                <span className="content-center">Quotation Additional Fee Amount:</span>
                <span>
                  <Input
                    className="text-right h-8"
                    type="number"
                    step={step}
                    {...register("quotationAdditionalFeeAmount", {
                      setValueAs: (value) => Number(value)
                    })}
                  />
                </span>
              </div>
              <div className="flex justify-between">
                <span className="content-center">Rounding Amount:</span>
                <span>
                  <Input
                    className="text-right h-8"
                    type="number"
                    step={step}
                    {...register("roundingAmount", {
                      setValueAs: (value) => Number(value)
                    })}
                  />
                </span>
              </div>
              <div className="flex justify-between font-bold">
                <span>Total Payable Amount:</span>
                <span>RM {formatTotalAmount(Number(totals?.totalPayableAmount || 0))}</span>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-4">
            <Button
              variant="outline"
              onClick={() => navigate("/quotations")}
              type="button"
            >
              Cancel
            </Button>
            {quotationId && (
              <Button
                variant="secondary"
                onClick={handlePrint}
                type="button"
                disabled={isPrinting}
              >
                {isPrinting ? (
                  <>Printing...</>
                ) : (
                  <>
                    <Printer className="mr-2 h-4 w-4" />
                    Print
                  </>
                )}
              </Button>
            )}
            <Button
              type="submit"
              disabled={createQuotation.isPending || updateQuotation.isPending}
            >
              {createQuotation.isPending || updateQuotation.isPending
                ? "Saving..."
                : quotationId
                ? "Update Quotation"
                : "Save Quotation"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
